import { useCallback, useContext, useMemo } from "react";
import type { Layout } from "react-grid-layout/legacy";
import type {
  ComponentSpec,
  ControllerKind,
  ProcessedWidgetData,
} from "../Processing/types";
import type { GridWidgetType } from "../types";
import {
  CONTROLLER_KINDS,
  bindableMiscs,
  controllerRuntimeId,
  controllerUuidsByKey,
  effectiveBinding,
  eligibleByKind,
  listControllers,
  slicerKeysInTemplate,
  templateText,
  type ControllerOption,
} from "../Processing/controllerBindings";
import type { SlicerValues } from "../Utils/resolveTemplate";
import {
  DashboardContext,
  WidgetDataContext,
  type DashboardContextValue,
  type DashboardListItem,
} from "./DashboardContext";
import type { AiWidgetState, WidgetEntry, WidgetDataStatus } from "./dashboardState";

function useDashboardContextInternal(): DashboardContextValue {
  const ctx = useContext(DashboardContext);
  if (!ctx)
    throw new Error(
      "Dashboard hooks must be used within a DashboardProvider",
    );
  return ctx;
}

/** Full context value -- prefer the narrower hooks below when possible. */
export function useDashboard(): DashboardContextValue {
  return useDashboardContextInternal();
}

/** Lifecycle action methods only. */
export function useDashboardActions(): {
  addWidget: (type: GridWidgetType, spec?: ComponentSpec) => void;
  duplicateWidget: (widgetId: string) => void;
  editWidget: (widgetId: string, spec: ComponentSpec) => void;
  patchWidgetDisplaySpec: (widgetId: string, spec: ComponentSpec) => void;
  removeWidget: (widgetId: string) => void;
  onLayoutChange: (layout: Layout) => void;
  loadDashboard: (dashboardId: string) => Promise<void>;
  renameDashboard: (name: string) => Promise<void>;
  createDashboard: () => Promise<void>;
  duplicateDashboard: () => Promise<void>;
  deleteDashboard: () => Promise<void>;
} {
  const {
    addWidget, duplicateWidget, editWidget, patchWidgetDisplaySpec, removeWidget,
    onLayoutChange, loadDashboard, renameDashboard, createDashboard,
    duplicateDashboard, deleteDashboard,
  } = useDashboardContextInternal();
  return {
    addWidget, duplicateWidget, editWidget, patchWidgetDisplaySpec, removeWidget,
    onLayoutChange, loadDashboard, renameDashboard, createDashboard,
    duplicateDashboard, deleteDashboard,
  };
}

/** Available dashboards for the selector. */
export function useDashboardList(): DashboardListItem[] {
  return useDashboardContextInternal().dashboardList;
}

/** Current grid layout array. */
export function useDashboardLayout(): Layout {
  return useDashboardContextInternal().state.layout;
}

/** All widget entries as an array. */
export function useDashboardWidgets(): WidgetEntry[] {
  return Object.values(useDashboardContextInternal().state.widgets);
}

/**
 * Processed data for a single widget.
 * Reads from WidgetDataContext (stable across layout-only changes)
 * so widgets don't re-render when only layout changes.
 */
export function useWidgetData(id: string): ProcessedWidgetData | null {
  const ctx = useContext(WidgetDataContext);
  if (!ctx) throw new Error("useWidgetData must be used within a DashboardProvider");
  return ctx.processedData[id] ?? null;
}

/**
 * Loading status for a single widget.
 * Reads from WidgetDataContext (stable across layout-only changes).
 */
export function useWidgetStatus(id: string): WidgetDataStatus {
  const ctx = useContext(WidgetDataContext);
  if (!ctx) throw new Error("useWidgetStatus must be used within a DashboardProvider");
  return ctx.widgetStatuses[id] ?? "loading";
}

/** True while a controller edit has this widget waiting for the autosave-triggered refetch. */
export function useWidgetRefreshing(id: string): boolean {
  const ctx = useContext(WidgetDataContext);
  if (!ctx) throw new Error("useWidgetRefreshing must be used within a DashboardProvider");
  return ctx.refreshingWidgetIds.has(id);
}

/** Error message for a single widget (if status is "error"). */
export function useWidgetError(id: string): string | null {
  const ctx = useContext(WidgetDataContext);
  if (!ctx) throw new Error("useWidgetError must be used within a DashboardProvider");
  return ctx.widgetErrors[id] ?? null;
}

const EMPTY_SLICER_VALUES: SlicerValues = Object.freeze({}) as SlicerValues;

/**
 * The slicer value map a `{Slicer.KEY.Variable}` token resolves against, keyed by slicer
 * key. Reads from WidgetDataContext, like `useWidgetData`, so a title does not re-render
 * on a layout drag. Empty outside a DashboardProvider, so the configurators stay usable.
 */
export function useSlicerVariables(): SlicerValues {
  const ctx = useContext(WidgetDataContext);
  return ctx?.slicerValues ?? EMPTY_SLICER_VALUES;
}

/** One AI tile's stream state, or null before its first turn starts. */
export function useAiWidget(widgetId: string): AiWidgetState | null {
  const { state } = useDashboardContextInternal();
  return state.aiWidgets[widgetId] ?? null;
}

/** Manual regenerate for one AI tile — the only caller that sends force: true. */
export function useRegenerateAiWidget(widgetId: string): () => void {
  const regenerate = useDashboardContextInternal().regenerateAiWidget;
  return useCallback(() => regenerate(widgetId), [regenerate, widgetId]);
}

// ── Slicers ──────────────────────────────────────

/**
 * Every slicer widget on the dashboard, in widget order. Returns an empty list
 * outside a DashboardProvider, so the shared date/subject forms stay usable anywhere.
 */
export function useControllers(): ControllerOption[] {
  const widgets = useContext(DashboardContext)?.state.widgets;
  return useMemo(() => (widgets ? listControllers(Object.values(widgets)) : []), [widgets]);
}

/** The uuids of the slicers a widget's title or text quotes with `{Slicer.KEY.Variable}`. */
function quotedControllerUuids(
  spec: ComponentSpec | undefined,
  uuidsByKey: Map<string, string>,
): string[] {
  const text = templateText(spec);
  if (!text) return [];
  const uuids: string[] = [];
  for (const key of slicerKeysInTemplate(text)) {
    const uuid = uuidsByKey.get(key);
    if (uuid) uuids.push(uuid);
  }
  return uuids;
}

/**
 * The controllers this widget follows, in widget order: the ones its sections bind, and
 * the ones its title or text quotes. Empty when it follows none. The info popover and
 * corner badges name them ("Controlled by Slicer: …").
 */
export function useWidgetControllers(id: string): ControllerOption[] {
  const spec = useContext(DashboardContext)?.state.widgets[id]?.spec;
  const controllers = useControllers();
  return useMemo(() => {
    if (!spec || controllers.length === 0) return [];
    const eligible = eligibleByKind(controllers);
    const bound = new Set<string>(quotedControllerUuids(spec, controllerUuidsByKey(controllers)));
    for (const misc of bindableMiscs(spec)) {
      for (const kind of CONTROLLER_KINDS) {
        const uuid = effectiveBinding(misc, kind, eligible[kind]);
        if (uuid) bound.add(uuid);
      }
    }
    return controllers.filter((c) => bound.has(c.uuid));
  }, [spec, controllers]);
}

/**
 * Which widgets follow each controller, split by the filter section they bind, plus the
 * widgets whose title or text quotes it in a token. `all` holds both.
 */
export type ControllerDependents =
  Record<ControllerKind, Set<string>> & { tokens: Set<string>; all: Set<string> };

/**
 * Controller UUID → the widgets it governs. Recomputed only when the widget map changes.
 * Drives the deletion guard, the controls-narrowing guard, and the Control Panel's skip count.
 */
export function useControllerDependents(): Record<string, ControllerDependents> {
  const { state } = useDashboardContextInternal();
  return useMemo(() => {
    const widgets = Object.values(state.widgets);
    const controllers = listControllers(widgets);
    if (controllers.length === 0) return {};

    const eligible = eligibleByKind(controllers);
    const uuidsByKey = controllerUuidsByKey(controllers);
    const index: Record<string, ControllerDependents> = {};
    const entry = (uuid: string) =>
      (index[uuid] ??= {
        date: new Set(), subject: new Set(), single_date: new Set(),
        single_player: new Set(), tokens: new Set(), all: new Set(),
      });

    for (const widget of widgets) {
      for (const misc of bindableMiscs(widget.spec)) {
        for (const kind of CONTROLLER_KINDS) {
          const uuid = effectiveBinding(misc, kind, eligible[kind]);
          if (!uuid) continue;
          entry(uuid)[kind].add(widget.id);
          entry(uuid).all.add(widget.id);
        }
      }
      for (const uuid of quotedControllerUuids(widget.spec, uuidsByKey)) {
        entry(uuid).tokens.add(widget.id);
        entry(uuid).all.add(widget.id);
      }
    }
    return index;
  }, [state.widgets]);
}

/**
 * Write a controller's picker state. This is an ordinary widget edit: it sets the
 * dashboard's saved values, so it needs edit permission and it saves through the normal
 * autosave path.
 */
export function useEditController(controllerUuid: string): (spec: ComponentSpec) => void {
  const { editWidget } = useDashboardContextInternal();
  return useCallback(
    (spec: ComponentSpec) => editWidget(controllerRuntimeId(controllerUuid), spec),
    [editWidget, controllerUuid],
  );
}
